/**
 * ========================================================
 * 🧱 LIBRERÍA CORE DE COMPONENTES - MEALCYCLE v1.0 (PRODUCCIÓN)
 * ========================================================
 * ✅ MATCH EXACTO: Sincronización 100% nativa con el productCatalog.
 * ✅ ESTANDARIZACIÓN: Unidades de consumo real ajustadas a UX.
 * ✅ MODULARIDAD: 152 bloques de construcción (Legos) listos para recetas.
 * ✅ CLEAN DATA: Cero duplicados, nombres limpios sin barras (/) y porciones calibradas matemáticamente.
 */
import { 
  COMPONENT_TYPES, 
  COMPONENT_TYPE_LIST,
  COMPONENT_TYPE_LABELS, 
  COMPONENT_TYPE_ICONS,
  COMPONENT_TYPE_COLORS,
  CATEGORIES, 
  UNITS 
} from './types';

// ============================================
// RE-EXPORTAR CONSTANTES PARA COMPATIBILIDAD
// ============================================
export { 
  COMPONENT_TYPES, 
  COMPONENT_TYPE_LIST,
  COMPONENT_TYPE_LABELS, 
  COMPONENT_TYPE_ICONS,
  COMPONENT_TYPE_COLORS
};

// ============================================
// CATEGORÍAS DE COMPONENTES
// ============================================
export const COMPONENT_CATEGORIES = {
  CARNES: CATEGORIES.CARNES,
  VEGETARIANO: CATEGORIES.VERDURAS,
  PASTAS: CATEGORIES.CEREALES,
  ENSALADAS: CATEGORIES.VERDURAS,
  GUARNICIONES: CATEGORIES.VERDURAS,
  DESAYUNO: CATEGORIES.CEREALES,
  BEBIDAS: CATEGORIES.BEBIDAS,
  SNACKS: CATEGORIES.SNACKS
};

export const COMPONENT_CATEGORY_LABELS = {
  [COMPONENT_CATEGORIES.CARNES]: 'Carnes',
  [COMPONENT_CATEGORIES.VEGETARIANO]: 'Vegetariano',
  [COMPONENT_CATEGORIES.PASTAS]: 'Pastas',
  [COMPONENT_CATEGORIES.ENSALADAS]: 'Ensaladas',
  [COMPONENT_CATEGORIES.GUARNICIONES]: 'Guarniciones',
  [COMPONENT_CATEGORIES.DESAYUNO]: 'Desayuno',
  [COMPONENT_CATEGORIES.BEBIDAS]: 'Bebidas',
  [COMPONENT_CATEGORIES.SNACKS]: 'Snacks'
};

// ============================================
// COMPONENTES BASE SIMPLES (150+)
// ============================================
export const SAMPLE_COMPONENTS = [

  // ==========================================
  // 🥤 BEBIDAS (14 componentes)
  // ==========================================
  {
    id: 'comp_cafe_leche',
    name: 'Café con Leche',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Café', quantity: 10, unit: UNITS.G, category: CATEGORIES.BEBIDAS },
      { name: 'Leche entera', quantity: 200, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Azúcar blanca', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_cafe_solo',
    name: 'Café Solo',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Café', quantity: 15, unit: UNITS.G, category: CATEGORIES.BEBIDAS },
      { name: 'Azúcar blanca', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_jugo_naranja',
    name: 'Jugo de Naranja',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Naranja', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Azúcar blanca', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_jugo_polvo',
    name: 'Jugo Preparado',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Jugo en polvo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.BEBIDAS },
      { name: 'Agua mineral', quantity: 1, unit: UNITS.L, category: CATEGORIES.BEBIDAS }
    ]
  },
  {
    id: 'comp_leche_chocolatada',
    name: 'Leche Chocolatada',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Leche entera', quantity: 250, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Cacao en polvo', quantity: 20, unit: UNITS.G, category: CATEGORIES.BEBIDAS },
      { name: 'Azúcar blanca', quantity: 15, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_licuado_banana',
    name: 'Licuado de Banana',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Banana', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Leche entera', quantity: 250, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Azúcar blanca', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_licuado_frutilla',
    name: 'Licuado de Frutilla',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Frutilla', quantity: 200, unit: UNITS.G, category: CATEGORIES.FRUTAS },
      { name: 'Leche entera', quantity: 250, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Azúcar blanca', quantity: 15, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_limonada',
    name: 'Limonada',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Limón', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Agua mineral', quantity: 1, unit: UNITS.L, category: CATEGORIES.BEBIDAS },
      { name: 'Azúcar blanca', quantity: 80, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_mate',
    name: 'Mate',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Yerba mate', quantity: 50, unit: UNITS.G, category: CATEGORIES.BEBIDAS },
      { name: 'Agua mineral', quantity: 1, unit: UNITS.L, category: CATEGORIES.BEBIDAS }
    ]
  },
  {
    id: 'comp_mate_cocido',
    name: 'Mate Cocido',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Yerba mate', quantity: 15, unit: UNITS.G, category: CATEGORIES.BEBIDAS },
      { name: 'Leche entera', quantity: 150, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Azúcar blanca', quantity: 15, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_smoothie_frutas',
    name: 'Smoothie de Frutas',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Banana', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Frutilla', quantity: 100, unit: UNITS.G, category: CATEGORIES.FRUTAS },
      { name: 'Yogur natural', quantity: 200, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Miel', quantity: 20, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_te_leche',
    name: 'Té con Leche',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Té', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.BEBIDAS },
      { name: 'Leche entera', quantity: 150, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Azúcar blanca', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_te_helado',
    name: 'Té Helado',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Té', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.BEBIDAS },
      { name: 'Limón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Azúcar blanca', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  {
    id: 'comp_te_solo',
    name: 'Té Solo',
    type: COMPONENT_TYPES.BEBIDAS,
    category: CATEGORIES.BEBIDAS,
    ingredients: [
      { name: 'Té', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.BEBIDAS },
      { name: 'Azúcar blanca', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  // ==========================================
  // 🥬 ENSALADAS (16 componentes)
  // ==========================================
  
  {
    id: 'comp_ensalada_arroz',
    name: 'Ensalada de Arroz',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Arroz blanco', quantity: 150, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Arvejas enlatadas', quantity: 80, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Choclo enlatado', quantity: 80, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Zanahoria', quantity: 80, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Mayonesa', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_brocoli',
    name: 'Ensalada de Brócoli',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Brócoli', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Tomate', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de oliva', quantity: 15, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_caprese',
    name: 'Ensalada Caprese',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tomate', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Queso por salut', quantity: 150, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Albahaca', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de oliva', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_caesar',
    name: 'Ensalada César',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Lechuga capuchina', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Pan lactal blanco', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Queso para rallar', quantity: 30, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Aderezo para ensaladas', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_chaucha',
    name: 'Ensalada de Chaucha',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Chaucha', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Tomate', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_criolla',
    name: 'Ensalada Criolla',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tomate', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Vinagre', quantity: 10, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_frutas',
    name: 'Ensalada de Frutas',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.FRUTAS,
    ingredients: [
      { name: 'Manzana', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Banana', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Naranja', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Azúcar blanca', quantity: 20, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_lenteja',
    name: 'Ensalada de Lentejas',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.LEGUMBRES,
    ingredients: [
      { name: 'Lentejas en lata', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.LEGUMBRES },
      { name: 'Tomate', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Choclo en lata', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.LEGUMBRES },
      { name: 'Cebolla', quantity: 50, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_mixta',
    name: 'Ensalada Mixta',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Lechuga', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Tomate', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 50, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_rusa',
    name: 'Ensalada Rusa',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Arvejas en lata', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.LEGUMBRES },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Mayonesa', quantity: 150, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_tomate',
    name: 'Ensalada de Tomate y Huevo',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tomate', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Aceite de girasol', quantity: 10, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Orégano', quantity: 2, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_ensalada_verde',
    name: 'Ensalada Verde',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Lechuga', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Rúcula', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de oliva', quantity: 15, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Aceto balsámico', quantity: 10, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_palta',
    name: 'Palta (Ensalada)',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.FRUTAS,
    ingredients: [
      { name: 'Palta', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Limón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Sal fina', quantity: 3, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_tomates_cherry',
    name: 'Ensalada de Tomates Cherry',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tomates cherry', quantity: 250, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de oliva', quantity: 10, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 2, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_ensalada_papa_huevo',
    name: 'Ensalada de Papa y Huevo',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Aceite de girasol', quantity: 15, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Mayonesa', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Perejil', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },

  {
    id: 'comp_ensalada_waldorf',
    name: 'Ensalada Waldorf',
    type: COMPONENT_TYPES.ENSALADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Manzana verde', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Apio', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Nueces', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Mayonesa', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Crema de leche', quantity: 50, unit: UNITS.ML, category: CATEGORIES.LACTEOS }
    ]
  },
  
  // ==========================================
  // ➕ EXTRAS (10 componentes)
  // ==========================================
  
  {
    id: 'comp_facturas',
    name: 'Facturas',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Facturas surtidas', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_galletitas',
    name: 'Galletitas',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.SNACKS,
    ingredients: [
      { name: 'Galletitas Dulces Surtidas', quantity: 100, unit: UNITS.G, category: CATEGORIES.SNACKS }
    ]
  },
  
  {
    id: 'comp_medialunas',
    name: 'Medialunas',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Medialunas', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_panqueques',
    name: 'Panqueques',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Harina 0000', quantity: 150, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Leche entera', quantity: 200, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Azúcar blanca', quantity: 20, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Dulce de leche', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_picada',
    name: 'Picada',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.FIAMBRES,
    ingredients: [
      { name: 'Jamón crudo', quantity: 100, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Salame', quantity: 100, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso en barra', quantity: 150, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Aceitunas', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Pan francés', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_sandwich_miga',
    name: 'Sandwich de Miga',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Pan lactal blanco', quantity: 6, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Jamón cocido', quantity: 50, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso en barra', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tostadas_dulce',
    name: 'Tostadas con Dulce',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tostadas de paquete', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Dulce de leche', quantity: 30, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tostadas_manteca',
    name: 'Tostadas con Manteca',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tostadas de paquete', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tostadas_mermelada',
    name: 'Tostadas con Mermelada',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tostadas de paquete', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Mermelada', quantity: 40, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_tostado_jyq',
    name: 'Tostado',
    type: COMPONENT_TYPES.EXTRAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Pan lactal blanco', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Jamón cocido', quantity: 50, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso en barra', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Manteca', quantity: 10, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  // ==========================================
  // 🚀 COMPONENTES FALTANTES AGREGADOS
  // ==========================================

  {
    id: 'comp_panchos',
    name: 'Panchos',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Salchichas de Viena', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.CARNES },
      { name: 'Pan de pancho', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Mayonesa', quantity: 20, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Mostaza', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_yogur_granola',
    name: 'Yogur con Granola',
    type: COMPONENT_TYPES.BEBIDAS, // o EXTRAS/POSTRES según tu lógica
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Yogur natural', quantity: 200, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Granola', quantity: 50, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Miel', quantity: 15, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  /// ==========================================
  // 🥗 GUARNICIONES (18 componentes)
  // ==========================================
  
  {
    id: 'comp_arroz_blanco',
    name: 'Arroz Blanco',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Arroz blanco', quantity: 100, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Aceite de girasol', quantity: 15, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_arroz_integral',
    name: 'Arroz Integral',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Arroz integral', quantity: 100, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Aceite de girasol', quantity: 15, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_batatas_fritas',
    name: 'Batatas Fritas',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Batata', quantity: 400, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_brocoli_vapor',
    name: 'Brócoli al Vapor',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Brócoli', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Sal fina', quantity: 3, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_choclo_parrilla',
    name: 'Choclo a la Parrilla',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Choclo', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_papas_fritas',
    name: 'Papas Fritas',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 100, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_papas_horno',
    name: 'Papas al Horno',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_papas_noisette',
    name: 'Papas Noisette',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papas noisette', quantity: 400, unit: UNITS.G, category: CATEGORIES.CONGELADOS },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_polenta',
    name: 'Polenta',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Polenta', quantity: 200, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Manteca', quantity: 30, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_polenta_grillada',
    name: 'Polenta Grillada',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Polenta', quantity: 200, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Manteca', quantity: 30, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Aceite de girasol', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pure',
    name: 'Puré de Papa',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Leche entera', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_pure_instantaneo',
    name: 'Puré de Papa (Instantáneo)',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.DESPENSA,
    ingredients: [
      { name: 'Puré de papa', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Leche entera', quantity: 250, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Agua mineral', quantity: 250, unit: UNITS.ML, category: CATEGORIES.BEBIDAS }
    ]
  },
  
  {
    id: 'comp_pure_mixto',
    name: 'Puré Mixto',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 250, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Batata', quantity: 250, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Leche entera', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_pure_zapallo',
    name: 'Puré de Zapallo',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Zapallo anco', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Leche entera', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_queso_rallado',
    name: 'Queso Rallado',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_verduras_grilladas',
    name: 'Verduras Grilladas',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Berenjena', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Zapallito', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de oliva', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_verduras_vapor',
    name: 'Verduras al Vapor',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Brócoli', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Coliflor', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Sal fina', quantity: 3, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_zapallitos_rellenos',
    name: 'Zapallitos Rellenos',
    type: COMPONENT_TYPES.GUARNICIONES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Zapallito', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Carne picada', quantity: 200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 50, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  // ==========================================
  // 🍲 GUISOS (8 componentes)
  // ==========================================
  
  {
    id: 'comp_carbonada',
    name: 'Carbonada',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Roast beef', quantity: 400, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Batata', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zapallo anco', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Choclo', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Arroz blanco', quantity: 100, unit: UNITS.G, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_estofado',
    name: 'Estofado',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Roast beef', quantity: 600, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Caldo en cubo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_guiso_arroz',
    name: 'Guiso de Arroz',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Arroz blanco', quantity: 300, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Carne picada', quantity: 200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Chorizo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_guiso_carrero',
    name: 'Guiso Carrero',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Roast beef', quantity: 300, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Batata', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Fideos cortos', quantity: 250, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Cebolla', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_guiso_lentejas',
    name: 'Guiso de Lentejas',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.LEGUMBRES,
    ingredients: [
      { name: 'Lentejas secas', quantity: 400, unit: UNITS.G, category: CATEGORIES.LEGUMBRES },
      { name: 'Panceta', quantity: 150, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Papa', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_guiso_mondongo',
    name: 'Guiso de Mondongo',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Falda', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Chorizo colorado', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FIAMBRES },
      { name: 'Papa', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Porotos blancos', quantity: 200, unit: UNITS.G, category: CATEGORIES.LEGUMBRES },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_locro',
    name: 'Locro',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Maíz blanco', quantity: 400, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Falda', quantity: 400, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Chorizo', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.CARNES },
      { name: 'Panceta', quantity: 150, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Porotos blancos', quantity: 200, unit: UNITS.G, category: CATEGORIES.LEGUMBRES },
      { name: 'Zapallo anco', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_puchero',
    name: 'Puchero',
    type: COMPONENT_TYPES.GUISOS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Osobuco', quantity: 800, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Chorizo', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Batata', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Choclo', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Zapallo anco', quantity: 400, unit: UNITS.G, category: CATEGORIES.VERDURAS }
    ]
  },

  // ==========================================
  // 🍞 PANES (4 componentes)
  // ==========================================
  
  {
    id: 'comp_pan_ajo',
    name: 'Pan de Ajo',
    type: COMPONENT_TYPES.PANES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Pan francés', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Ajo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Manteca', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Perejil', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_pan_casero',
    name: 'Pan Casero',
    type: COMPONENT_TYPES.PANES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Harina 0000', quantity: 500, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Levadura', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Aceite de girasol', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pan_integral',
    name: 'Pan Integral',
    type: COMPONENT_TYPES.PANES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Harina integral', quantity: 500, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Levadura', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Aceite de girasol', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pan_frances_solo',
    name: 'Pan Francés',
    type: COMPONENT_TYPES.PANES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Pan francés', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES }
    ]
  },
  
  // ==========================================
  // 🍝 PASTAS (10 componentes)
  // ==========================================
  
  {
    id: 'comp_canelones',
    name: 'Canelones',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Harina 0000', quantity: 200, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Ricota', quantity: 400, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Espinaca', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_fideos',
    name: 'Fideos',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Fideos largos', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_lasagna',
    name: 'Lasagna',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Fideos largos', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Carne picada', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Queso mozzarella', quantity: 250, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Jamón cocido', quantity: 150, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_noquis',
    name: 'Ñoquis',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Ñoquis', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_ravioles',
    name: 'Ravioles',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Pastas rellenas', quantity: 1, unit: UNITS.CAJA, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_sorrentinos',
    name: 'Sorrentinos',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Pastas rellenas', quantity: 1, unit: UNITS.CAJA, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_tallarines',
    name: 'Tallarines',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Fideos largos', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_spaguetti',
    name: 'Spaguetti',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Fideos largos', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_penne',
    name: 'Penne',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Fideos cortos', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_mostacholes',
    name: 'Mostacholes',
    type: COMPONENT_TYPES.PASTAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Fideos cortos', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  // ==========================================
  // 🍰 POSTRES (6 componentes)
  // ==========================================
  
  {
    id: 'comp_arroz_leche',
    name: 'Arroz con Leche',
    type: COMPONENT_TYPES.POSTRES,
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Arroz blanco', quantity: 150, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Leche entera', quantity: 1, unit: UNITS.L, category: CATEGORIES.LACTEOS },
      { name: 'Azúcar blanca', quantity: 150, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Canela', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_budin_pan',
    name: 'Budín de Pan',
    type: COMPONENT_TYPES.POSTRES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Pan francés', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Leche entera', quantity: 500, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Azúcar blanca', quantity: 150, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Pasas de uva', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_flan_casero',
    name: 'Flan Casero',
    type: COMPONENT_TYPES.POSTRES,
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Leche entera', quantity: 500, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Huevos', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Azúcar blanca', quantity: 150, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Esencia de vainilla', quantity: 5, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_flan_cajita',
    name: 'Flan (de polvo)',
    type: COMPONENT_TYPES.POSTRES,
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Flan en polvo', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.DESPENSA },
      { name: 'Leche entera', quantity: 1, unit: UNITS.L, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_panqueques_ddl',
    name: 'Panqueques con Dulce de Leche',
    type: COMPONENT_TYPES.POSTRES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Harina 0000', quantity: 150, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Leche entera', quantity: 200, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Dulce de leche', quantity: 100, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_vigilante',
    name: 'Vigilante (Queso y Dulce)',
    type: COMPONENT_TYPES.POSTRES,
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Queso cuartirolo', quantity: 150, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Dulce de batata', quantity: 150, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  // ==========================================
  // 🍖 PRINCIPALES (28 componentes)
  // ==========================================
  {
    id: 'comp_wrap_pollo',
    name: 'Wrap de Pollo',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Tortillas de trigo', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Pechuga de pollo', quantity: 150, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Lechuga', quantity: 50, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Tomate', quantity: 0.5, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Queso crema', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_bife_cacerola',
    name: 'Bife a la Cacerola',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Nalga', quantity: 200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Puré de tomate', quantity: 100, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Cebolla', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 0.5, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Papa', quantity: 250, unit: UNITS.G, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_wok_fideos',
    name: 'Wok de Fideos con Pollo',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Fideos', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Pechuga de pollo', quantity: 100, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Salsa de soja', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Cebolla', quantity: 0.5, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 0.5, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_albondigas',
    name: 'Albóndigas',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Carne picada', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Pan rallado', quantity: 80, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Huevos', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Ajo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Perejil', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_asado_tira',
    name: 'Asado de Tira',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Asado', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_bife',
    name: 'Bife',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Cuadril', quantity: 200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Aceite de girasol', quantity: 10, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_bife_chorizo',
    name: 'Bife de Chorizo',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Bife de chorizo', quantity: 300, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_brochetas_pollo',
    name: 'Brochetas de Pollo',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pechuga de pollo', quantity: 300, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Morrón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_choripan',
    name: 'Choripán',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Chorizo', quantity: 150, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Pan francés', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_costeletas',
    name: 'Costeletas de Cerdo',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Costeletas de cerdo', quantity: 400, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Sal fina', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Aceite de girasol', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_entraña',
    name: 'Entraña',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Entraña', quantity: 250, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Sal gruesa', quantity: 10, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_hamburguesa',
    name: 'Hamburguesa (Casera)',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Carne picada', quantity: 150, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 30, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Pan de hamburguesa', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES }
    ]
  },
  
  {
    id: 'comp_matambre_pizza',
    name: 'Matambre a la Pizza',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Matambre', quantity: 400, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Salsa de tomate lista', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Queso mozzarella', quantity: 150, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Jamón cocido', quantity: 100, unit: UNITS.G, category: CATEGORIES.FIAMBRES }
    ]
  },
  
  {
    id: 'comp_medallones',
    name: 'Medallones de Carne y Avena',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Carne picada', quantity: 400, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Avena', quantity: 50, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Huevos', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Zanahoria', quantity: 80, unit: UNITS.G, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_milanesa',
    name: 'Milanesa',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Nalga', quantity: 150, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Huevos', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Pan rallado', quantity: 80, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_milanesa_napolitana',
    name: 'Milanesa Napolitana',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Nalga', quantity: 150, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Huevos', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Pan rallado', quantity: 80, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Salsa de tomate lista', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Queso mozzarella', quantity: 80, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Jamón cocido', quantity: 50, unit: UNITS.G, category: CATEGORIES.FIAMBRES }
    ]
  },
  
  {
    id: 'comp_marinera_rebozada',
    name: 'Marinera de Pescado',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Filet de merluza', quantity: 200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Huevos', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Pan rallado', quantity: 80, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_pata_muslo_horno',
    name: 'Pata Muslo al Horno',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pata muslo', quantity: 400, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Limón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Aceite de girasol', quantity: 15, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal gruesa', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Orégano', quantity: 2, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pechugas_rellenas',
    name: 'Pechugas Rellenas',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pechuga de pollo', quantity: 250, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Jamón cocido', quantity: 50, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso en barra', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_pescado_horno',
    name: 'Pescado al Horno',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Filet de merluza', quantity: 250, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Limón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Aceite de oliva', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pizza',
    name: 'Pizza de Muzzarella',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Prepizza', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Queso mozzarella', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Salsa de tomate lista', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Orégano', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pollo_grillado',
    name: 'Pollo Grillado',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pechuga de pollo', quantity: 250, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Limón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pollo_horno',
    name: 'Pollo al Horno con Papas',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pollo entero', quantity: 1200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Limón', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Aceite de girasol', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salmon',
    name: 'Pescado Grillado',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Filet de merluza', quantity: 200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Limón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Aceite de oliva', quantity: 15, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_sandwich_milanesa',
    name: 'Sándwich de Milanesa',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Nalga', quantity: 150, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Pan francés', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Lechuga', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Tomate', quantity: 50, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Mayonesa', quantity: 20, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_suprema_maryland',
    name: 'Suprema Maryland',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pechuga de pollo', quantity: 250, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Huevos', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Pan rallado', quantity: 80, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Banana', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Durazno enlatado', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS }
    ]
  },
  
  {
    id: 'comp_tacos',
    name: 'Tacos de Carne',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Carne picada', quantity: 200, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Rapiditas', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Cebolla', quantity: 50, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Tomate', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_torrejitas_acelga',
    name: 'Torrejitas de Acelga',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Acelga', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Harina 0000', quantity: 100, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Queso rallado', quantity: 80, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_torrejitas_arroz',
    name: 'Torrejitas de Arroz',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Arroz blanco', quantity: 150, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Queso rallado', quantity: 80, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_tortilla_papa',
    name: 'Tortilla de Papa',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 500, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 6, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 100, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_vacio_horno',
    name: 'Vacío al Horno',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Vacío', quantity: 600, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Sal gruesa', quantity: 15, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Aceite de girasol', quantity: 20, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_pastel_papa',
    name: 'Pastel de Papa',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Carne picada', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 1000, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Aceitunas', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_empanadas_carne',
    name: 'Empanadas de Carne',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Tapas de empanada', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Carne picada', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Aceitunas', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_empanadas_jyq',
    name: 'Empanadas de Jamón y Queso',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tapas de empanada', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Jamón cocido', quantity: 200, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso cremoso', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },

  {
    id: 'comp_tarta_jyq',
    name: 'Tarta de Jamón y Queso',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Jamón cocido', quantity: 200, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso cremoso', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS }
    ]
  },

  {
    id: 'comp_tarta_pascualina',
    name: 'Tarta Pascualina',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Acelga', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },

  {
    id: 'comp_arroz_pollo',
    name: 'Arroz con Pollo',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pata muslo', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Arroz blanco', quantity: 300, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Cebolla', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_suprema_pollo',
    name: 'Suprema de Pollo',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pechuga de pollo', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Pan rallado', quantity: 150, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Huevos', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_bife_criolla',
    name: 'Bifes a la Criolla',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Cuadril', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Papa', quantity: 400, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_cerdo_mostaza',
    name: 'Cerdo a la Mostaza',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Carré de cerdo', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Mostaza', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Crema de leche', quantity: 200, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS }
    ]
  },

  {
    id: 'comp_bondiola_cerveza',
    name: 'Bondiola a la Cerveza',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Bondiola', quantity: 1000, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cerveza', quantity: 1, unit: UNITS.L, category: CATEGORIES.BEBIDAS },
      { name: 'Cebolla', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Mostaza', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_omelette',
    name: 'Omelette de Jamón y Queso',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.HUEVOS,
    ingredients: [
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Queso en barra', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Jamón cocido', quantity: 50, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Aceite de girasol', quantity: 10, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },

  {
    id: 'comp_berenjenas_rellenas',
    name: 'Berenjenas Rellenas',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Berenjena', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Carne picada', quantity: 300, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Queso cremoso', quantity: 150, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },

  {
    id: 'comp_pizza_casera',
    name: 'Pizza Casera',
    type: COMPONENT_TYPES.PRINCIPALES,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Harina 0000', quantity: 500, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Levadura', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.CEREALES },
      { name: 'Queso mozzarella', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Salsa de tomate lista', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA }
    ]
  },
  
  // ==========================================
  // 🥫 SALSAS (10 componentes)
  // ==========================================
  
  {
    id: 'comp_chimichurri',
    name: 'Chimichurri',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.DESPENSA,
    ingredients: [
      { name: 'Perejil', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Ajo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 100, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Vinagre', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Orégano', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Ají molido', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salsa_blanca',
    name: 'Salsa Blanca',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Leche entera', quantity: 500, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Harina 0000', quantity: 50, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Manteca', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Sal fina', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Pimienta', quantity: 2, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salsa_bolognesa',
    name: 'Salsa Bolognesa',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Carne picada', quantity: 300, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Puré de tomate', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 80, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salsa_tomate',
    name: 'Salsa de Tomate',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.DESPENSA,
    ingredients: [
      { name: 'Tomate triturado', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Ajo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Orégano', quantity: 5, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salsa_golf',
    name: 'Salsa Golf Casera',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.DESPENSA,
    ingredients: [
      { name: 'Mayonesa', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Ketchup', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Crema de leche', quantity: 20, unit: UNITS.ML, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_salsa_criolla',
    name: 'Salsa Criolla',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tomate', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Vinagre', quantity: 30, unit: UNITS.ML, category: CATEGORIES.DESPENSA },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salsa_crema',
    name: 'Salsa de Crema',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.LACTEOS,
    ingredients: [
      { name: 'Crema de leche', quantity: 200, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Sal fina', quantity: 3, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salsa_hongos',
    name: 'Salsa de Hongos',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Hongos', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Crema de leche', quantity: 150, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Cebolla', quantity: 50, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Manteca', quantity: 20, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_salsa_pesto',
    name: 'Salsa Pesto',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Albahaca', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Ajo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Nueces', quantity: 50, unit: UNITS.G, category: CATEGORIES.DESPENSA },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Aceite de oliva', quantity: 100, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_salsa_portuguesa',
    name: 'Salsa Portuguesa',
    type: COMPONENT_TYPES.SALSAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tomate', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Morrón', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Ajo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Aceite de girasol', quantity: 50, unit: UNITS.ML, category: CATEGORIES.DESPENSA }
    ]
  },
  
  // ==========================================
  // 🥟 TARTAS Y EMPANADAS (10 componentes)
  // ==========================================
  
  {
    id: 'comp_empanada_carne',
    name: 'Empanadas de Carne',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tapas de empanada', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Carne picada', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Aceitunas', quantity: 100, unit: UNITS.G, category: CATEGORIES.DESPENSA }
    ]
  },
  
  {
    id: 'comp_empanada_jyq',
    name: 'Empanadas de Jamón y Queso',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tapas de empanada', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Jamón cocido', quantity: 200, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso cremoso', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_empanada_pollo',
    name: 'Empanadas de Pollo',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tapas de empanada', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Pechuga de pollo', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Crema de leche', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_empanada_arabe',
    name: 'Empanadas Árabes',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tapas de empanada', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Carne picada', quantity: 500, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 300, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Limón', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.FRUTAS },
      { name: 'Perejil', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_pascualina',
    name: 'Tarta Pascualina',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Acelga', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Ricota', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Huevos', quantity: 4, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tarta_atun',
    name: 'Tarta de Atún',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Atún enlatado', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Crema de leche', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tarta_espinaca',
    name: 'Tarta de Espinaca',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Espinaca', quantity: 2, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Ricota', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tarta_jyq',
    name: 'Tarta de Jamón y Queso',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.CEREALES,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Jamón cocido', quantity: 200, unit: UNITS.G, category: CATEGORIES.FIAMBRES },
      { name: 'Queso cremoso', quantity: 300, unit: UNITS.G, category: CATEGORIES.LACTEOS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Crema de leche', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tarta_pollo',
    name: 'Tarta de Pollo',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Pechuga de pollo', quantity: 400, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Cebolla', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Crema de leche', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS }
    ]
  },
  
  {
    id: 'comp_tarta_verdura',
    name: 'Tarta de Verduras',
    type: COMPONENT_TYPES.TARTAS_EMPANADAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Tapas de tarta', quantity: 1, unit: UNITS.PAQUETE, category: CATEGORIES.CEREALES },
      { name: 'Acelga', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Espinaca', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Huevos', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.HUEVOS },
      { name: 'Queso rallado', quantity: 50, unit: UNITS.G, category: CATEGORIES.LACTEOS }
    ]
  },

  // ==========================================
  // 🍜 SOPAS (5 componentes)
  // ==========================================
  
  {
    id: 'comp_caldo_gallina',
    name: 'Caldo de Pollo',
    type: COMPONENT_TYPES.SOPAS,
    category: CATEGORIES.CARNES,
    ingredients: [
      { name: 'Pollo entero', quantity: 800, unit: UNITS.G, category: CATEGORIES.CARNES },
      { name: 'Zanahoria', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Apio', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Puerro', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_crema_calabaza',
    name: 'Crema de Calabaza',
    type: COMPONENT_TYPES.SOPAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Zapallo anco', quantity: 600, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Crema de leche', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Caldo en cubo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Agua mineral', quantity: 500, unit: UNITS.ML, category: CATEGORIES.BEBIDAS }
    ]
  },
  
  {
    id: 'comp_crema_choclo',
    name: 'Crema de Choclo',
    type: COMPONENT_TYPES.SOPAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Choclo', quantity: 3, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Crema de leche', quantity: 100, unit: UNITS.ML, category: CATEGORIES.LACTEOS },
      { name: 'Caldo en cubo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Agua mineral', quantity: 500, unit: UNITS.ML, category: CATEGORIES.BEBIDAS }
    ]
  },
  
  {
    id: 'comp_minestrone',
    name: 'Minestrone',
    type: COMPONENT_TYPES.SOPAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Fideos cortos', quantity: 100, unit: UNITS.G, category: CATEGORIES.CEREALES },
      { name: 'Zanahoria', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zapallo anco', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Porotos blancos', quantity: 150, unit: UNITS.G, category: CATEGORIES.LEGUMBRES },
      { name: 'Tomate', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS }
    ]
  },
  
  {
    id: 'comp_sopa_verduras',
    name: 'Sopa de Verduras',
    type: COMPONENT_TYPES.SOPAS,
    category: CATEGORIES.VERDURAS,
    ingredients: [
      { name: 'Papa', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zanahoria', quantity: 150, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Zapallo anco', quantity: 200, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Cebolla', quantity: 100, unit: UNITS.G, category: CATEGORIES.VERDURAS },
      { name: 'Apio', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.VERDURAS },
      { name: 'Caldo en cubo', quantity: 1, unit: UNITS.UNIDAD, category: CATEGORIES.DESPENSA },
      { name: 'Agua mineral', quantity: 1, unit: UNITS.L, category: CATEGORIES.BEBIDAS }
    ]
  }
  
];

// ============================================
// VALIDACIÓN Y HELPERS
// ============================================

/**
 * Valida que un componente tenga la estructura correcta
 */
export const validateComponent = (component) => {
  const errors = [];
  
  if (!component.id) errors.push('ID es requerido');
  if (!component.name) errors.push('Nombre es requerido');
  if (!component.type) errors.push('Tipo es requerido');
  if (!component.category) errors.push('Categoría es requerida');
  if (!component.ingredients || component.ingredients.length === 0) {
    errors.push('Debe tener al menos un ingrediente');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Busca componentes por tipo
 */
export const getComponentsByType = (type) => {
  return SAMPLE_COMPONENTS.filter(c => c.type === type);
};

/**
 * Busca componentes por categoría
 */
export const getComponentsByCategory = (category) => {
  return SAMPLE_COMPONENTS.filter(c => c.category === category);
};

/**
 * Busca un componente por ID
 */
export const getComponentById = (id) => {
  return SAMPLE_COMPONENTS.find(c => c.id === id);
};

/**
 * Busca componentes por nombre (búsqueda parcial)
 */
export const searchComponents = (query) => {
  const lowerQuery = query.toLowerCase();
  return SAMPLE_COMPONENTS.filter(c => 
    c.name.toLowerCase().includes(lowerQuery)
  );
};

// ============================================
// ESTADÍSTICAS
// ============================================

export const COMPONENT_STATS = {
  total: SAMPLE_COMPONENTS.length,
  byType: COMPONENT_TYPE_LIST.reduce((acc, type) => {
    acc[type] = SAMPLE_COMPONENTS.filter(c => c.type === type).length;
    return acc;
  }, {}),
  byCategory: Object.values(CATEGORIES).reduce((acc, cat) => {
    acc[cat] = SAMPLE_COMPONENTS.filter(c => c.category === cat).length;
    return acc;
  }, {})
};

// ============================================
// EXPORT DEFAULT
// ============================================

export default {
  SAMPLE_COMPONENTS,
  COMPONENT_CATEGORIES,
  COMPONENT_CATEGORY_LABELS,
  validateComponent,
  getComponentsByType,
  getComponentsByCategory,
  getComponentById,
  searchComponents,
  COMPONENT_STATS
};

console.log('✅ Componentes v4.1 cargados:', SAMPLE_COMPONENTS.length, 'componentes simples');
console.log('📊 Distribución por tipo:', COMPONENT_STATS.byType);